import { useEffect, useState } from "react";
import api, { dkk } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Clock, Sunset, Wallet } from "lucide-react";

const NORMAL = "#38BDF8";
const LATE = "#FB923C";
const LATE_FROM = 17; // highlight from 17:00

const dec1 = (n) => (n || 0).toLocaleString("da-DK", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const pctLabel = (p) => (p > 0 && p < 0.5 ? "<1%" : `${Math.round(p)}%`);

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const hourLabel = (h) => `${String(h).padStart(2, "0")}`;
const ANALYSIS_FROM = "2026-07-01"; // åbningstidsanalyse regnes fra 1. juli
const REFRESH_MS = 5 * 60 * 1000;

function HourTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-3 py-2 text-xs">
      <p className="text-slate-500 mb-1 font-medium">Kl. {hourLabel(p.hour)}–{hourLabel(p.hour + 1)}</p>
      <p className="text-slate-500">{dkk(p.avg_revenue)} / dag</p>
      <p className="text-slate-500">{dec1(p.avg_orders)} kunder / dag</p>
    </div>
  );
}

export function HourlyStats({ store }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wage, setWage] = useState("160");
  const [margin, setMargin] = useState("25");

  useEffect(() => {
    let active = true;
    const fetchData = (showLoading) => {
      if (showLoading) setLoading(true);
      return api.get("/kpi/hourly", { params: { from: ANALYSIS_FROM, to: iso(new Date()), store } })
        .then(({ data }) => { if (active) setData(data); })
        .catch(() => { if (active && showLoading) setData(null); })
        .finally(() => { if (active && showLoading) setLoading(false); });
    };
    fetchData(true);
    const id = setInterval(() => {
      api.post("/sync/live").catch(() => {}).finally(() => fetchData(false));
    }, REFRESH_MS);
    return () => { active = false; clearInterval(id); };
  }, [store]);

  if (loading) return <p className="text-slate-500 text-sm py-8 text-center" data-testid="hourly-loading">Henter…</p>;
  if (!data || !data.hours?.length) {
    return (
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid="hourly-stats">
        <p className="text-slate-500 text-sm py-8 text-center" data-testid="hourly-empty">Ingen betalingsdata for perioden endnu.</p>
      </div>
    );
  }

  const hours = data.hours;
  const totalAvg = hours.reduce((s, h) => s + h.avg_revenue, 0) || 1;
  const at = (h) => hours.find((x) => x.hour === h);
  const windowRev = (from, to) => hours.filter((h) => h.hour >= from && h.hour < to).reduce((s, h) => s + h.avg_revenue, 0);
  const windowOrd = (from, to) => hours.filter((h) => h.hour >= from && h.hour < to).reduce((s, h) => s + h.avg_orders, 0);
  const eveningShare = Math.round((windowRev(LATE_FROM, 24) / totalAvg) * 100);
  const lastHour = hours[hours.length - 1].hour;

  const windows = [
    { label: "17–18", from: 17, to: 18 },
    { label: "18–19", from: 18, to: 19 },
    { label: "19–20", from: 19, to: 20 },
  ];

  const wageNum = parseFloat(String(wage).replace(",", ".")) || 0;
  const marginNum = (parseFloat(String(margin).replace(",", ".")) || 0) / 100;
  const contribution = (rev) => (rev / 1.25) * marginNum;
  const covers = (rev) => wageNum > 0 && contribution(rev) >= wageNum;

  return (
    <div className="space-y-4" data-testid="hourly-stats">
      <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="hourly-header">
        <Clock className="w-4 h-4" />
        <span>Salg pr. time · gennemsnit pr. dag · siden 1. juli ({data.days} dage med salg) · {store === "all" ? "begge butikker" : store} · opdateres hvert 5. min</span>
      </div>

      {/* Løn-tjek & anbefalet lukketid */}
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid="wage-check">
        <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1 flex items-center gap-2"><Wallet className="w-4 h-4" /> Løn-tjek & anbefalet lukketid</h3>
        <p className="text-xs text-slate-500 mb-4">Indtast timeløn og dækningsgrad. Grøn time = dækningsbidraget (omsætning ekskl. moms × dækningsgrad) dækker lønnen; rød = timen giver underskud.</p>
        <div className="flex flex-wrap gap-4 mb-5">
          <label className="text-sm text-slate-500">
            <span className="block text-xs text-slate-500 mb-1">Timeløn (kr/time)</span>
            <Input value={wage} onChange={(e) => setWage(e.target.value)} className="h-9 w-32 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="wage-input" />
          </label>
          <label className="text-sm text-slate-500">
            <span className="block text-xs text-slate-500 mb-1">Dækningsgrad (%)</span>
            <Input value={margin} onChange={(e) => setMargin(e.target.value)} className="h-9 w-28 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="margin-input" />
          </label>
        </div>

        {(data.by_store || []).map((st) => {
          const cols = (st.weekdays[0]?.hours || []).map((h) => h.hour);
          const stRecos = st.weekdays.map((wd) => {
            const covered = wd.hours.filter((h) => covers(h.avg_revenue)).map((h) => h.hour);
            return { weekday: wd.weekday, label: wd.label, closeHour: covered.length ? Math.max(...covered) + 1 : null };
          });
          return (
            <div key={st.store} className="mb-6 last:mb-0" data-testid={`wage-store-${st.store}`}>
              <div className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: st.store === "falster" ? "#FB923C" : "#38BDF8" }} />{st.label}
              </div>
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">Anbefalet lukketid</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-4" data-testid={`close-recos-${st.store}`}>
                {stRecos.map((r) => (
                  <div key={r.weekday} className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-3 py-2 text-center" data-testid={`close-reco-${st.store}-${r.weekday}`}>
                    <div className="text-xs text-slate-500">{r.label.slice(0, 3)}</div>
                    <div className="text-sm font-semibold" style={{ color: r.closeHour ? "#34D399" : "#F87171" }}>
                      {r.closeHour ? `Luk ${hourLabel(r.closeHour)}` : "Ingen"}
                    </div>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="text-xs border-separate" style={{ borderSpacing: "3px" }} data-testid={`wage-heatmap-${st.store}`}>
                  <thead>
                    <tr>
                      <th className="text-left text-slate-500 font-medium pr-2"></th>
                      {cols.map((h) => (
                        <th key={h} className="text-slate-500 font-medium w-8">{hourLabel(h)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {st.weekdays.map((wd) => (
                      <tr key={wd.weekday}>
                        <td className="text-slate-500 pr-2 whitespace-nowrap">{wd.label.slice(0, 3)}</td>
                        {cols.map((H) => {
                          const cell = wd.hours.find((x) => x.hour === H);
                          const rev = cell?.avg_revenue || 0;
                          const ok = covers(rev);
                          const has = rev > 0;
                          return (
                            <td key={H} title={`${wd.label} kl. ${hourLabel(H)}: ${dkk(rev)}/dag · bidrag ${dkk(contribution(rev))}`}>
                              <div className="w-8 h-7 rounded flex items-center justify-center" style={{ background: !has ? "#e2e8f0" : ok ? "rgba(52,211,153,0.22)" : "rgba(248,113,113,0.18)" }}>
                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: !has ? "#334155" : ok ? "#34D399" : "#F87171" }} />
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      {/* Evening window insight cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {windows.map((w) => (
          <div key={w.label} className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid={`hourly-window-${w.from}`}>
            <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2 flex items-center gap-2"><Sunset className="w-4 h-4" /> Kl. {w.label}</div>
            <div className="font-heading text-2xl font-light tabular-nums text-slate-900">{dkk(windowRev(w.from, w.to))}</div>
            <div className="text-xs text-slate-500 mt-1">{dec1(windowOrd(w.from, w.to))} kunder / dag i snit</div>
          </div>
        ))}
        <div className="rounded-2xl bg-[#fef6e7] border border-[#fcd34d] p-5" data-testid="hourly-evening-share">
          <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-amber-600/80 mb-2">Efter kl. 17</div>
          <div className="font-heading text-2xl font-light tabular-nums text-amber-600">{eveningShare}%</div>
          <div className="text-xs text-amber-600/70 mt-1">af dagens omsætning</div>
        </div>
      </div>

      {/* Hourly bar chart */}
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid="hourly-chart">
        <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500 mb-4">Omsætning pr. time (gns. pr. dag)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={hours} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="hour" tickFormatter={hourLabel} tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
            <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} width={52} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
            <Tooltip content={<HourTip />} cursor={{ fill: "#ffffff08" }} />
            <Bar dataKey="avg_revenue" radius={[4, 4, 0, 0]}>
              {hours.map((h) => (
                <Cell key={h.hour} fill={h.hour >= LATE_FROM ? LATE : NORMAL} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className="flex items-center gap-4 text-xs text-slate-500 mt-3">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: NORMAL }} /> Dagtimer</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: LATE }} /> Aften (efter 17)</span>
        </div>
      </div>

      {/* Aftenhandel pr. ugedag */}
      {data.weekdays?.some((w) => w.days > 0) && (
        <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid="hourly-weekday">
          <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1">Aftenhandel pr. ugedag (efter kl. 17)</h3>
          <p className="text-xs text-slate-500 mb-4">Gennemsnitlig aften-omsætning pr. dag — se om fx fredag har mere aftenhandel end mandag.</p>
          <ul className="space-y-3">
            {(() => {
              const maxEve = Math.max(...data.weekdays.map((w) => w.avg_evening_revenue), 1);
              return data.weekdays.filter((w) => w.days > 0).map((w) => (
                <li key={w.weekday} data-testid={`hourly-weekday-${w.weekday}`}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="text-slate-700 w-20">{w.label}</span>
                    <span className="tabular-nums text-slate-800">
                      <span className="text-slate-500 mr-3">{dec1(w.avg_evening_orders)} kunder</span>
                      <span className="text-slate-500 mr-3">{pctLabel(w.evening_share)}</span>
                      {dkk(w.avg_evening_revenue)}
                    </span>
                  </div>
                  <div className="h-2 rounded bg-[#e2e8f0] overflow-hidden">
                    <div className="h-full rounded" style={{ width: `${(w.avg_evening_revenue / maxEve) * 100}%`, background: LATE }} />
                  </div>
                </li>
              ));
            })()}
          </ul>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[420px]" data-testid="hourly-table">
            <thead>
              <tr className="text-slate-500 text-xs uppercase tracking-wider border-b border-[#e2e8f0]">
                <th className="text-left font-medium py-2 pr-2">Time</th>
                <th className="text-right font-medium py-2 pr-2">Kunder / dag</th>
                <th className="text-right font-medium py-2 pr-2">Omsætning / dag</th>
                <th className="text-right font-medium py-2">Andel</th>
              </tr>
            </thead>
            <tbody>
              {hours.map((h) => (
                <tr key={h.hour} className={`border-b border-[#e2e8f0] ${h.hour >= LATE_FROM ? "text-amber-700/90" : "text-slate-700"}`} data-testid={`hourly-row-${h.hour}`}>
                  <td className="py-2 pr-2">Kl. {hourLabel(h.hour)}–{hourLabel(h.hour + 1)}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{dec1(h.avg_orders)}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{dkk(h.avg_revenue)}</td>
                  <td className="py-2 text-right tabular-nums text-slate-500">{pctLabel((h.avg_revenue / totalAvg) * 100)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Butikken har sidst salg omkring kl. {hourLabel(lastHour)}. Sammenhold aften-timernes omsætning med lønnen for at holde åbent — hvis kl. 19–20 giver mindre end en medarbejder koster i den time, betaler det sig sjældent.
        </p>
      </div>
    </div>
  );
}
